// 작품·회차 서버 상태 — React Query. 화면은 이 훅만 알고 API 주소·토큰 처리는 모른다
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { sessionStore, useSession, useTrack } from '../../../composition/plugin-runtime';
import { SerialApiError, type SerialApi } from '../adapters/serial-api';
import { errorMessageKey } from '../domain/serial';

export type { SerialApi };

const KEY = 'serial';

/** 4xx(없음·권한·형식)는 다시 불러도 같다 — 재시도는 네트워크·5xx 에만 2번까지. 내린 회차를 3번 다시 불러 화면이 멈추던 것 */
function retry(count: number, error: unknown): boolean {
  if (error instanceof SerialApiError && !error.retryable) return false;
  return count < 2;
}

/** 쓰기 전에 로그인 여부만 본다 — 토큰은 전송(rpcTransport)이 부르는 시점의 것을 싣는다. 없으면 서버까지 가지 않는다 */
function requireLogin(): void {
  if (!sessionStore.get()) throw new SerialApiError('login_required', false);
}

/**
 * 내린 회차·작품의 캐시를 버린다. 보고 있는 화면이 있으면 그 화면이 떠날 때 버린다.
 * 바로 버리면, 데이터 라우터가 화면 이동을 끝내기 전에 지금 화면이 한 번 더 그려지면서 빈 캐시를 다시 채우려고 404 를 받는다(2026-10-21 운영)
 */
function dropWhenUnwatched(client: QueryClient, queryKey: readonly unknown[]): void {
  const cache = client.getQueryCache();
  for (const query of cache.findAll({ queryKey })) {
    if (query.getObserversCount() === 0) {
      cache.remove(query);
      continue;
    }
    void client.cancelQueries({ queryKey: query.queryKey, exact: true });
    const unsubscribe = cache.subscribe(event => {
      if (event.type !== 'observerRemoved' || event.query !== query || query.getObserversCount() > 0) return;
      unsubscribe();
      cache.remove(query);
    });
  }
}

/** 오류 → 문구 키와 덧붙은 값(상한 등). 화면은 t(key) 로 문구를 고른다 */
export function describeError(error: unknown): { key: string; detail: Readonly<Record<string, unknown>> } {
  if (error instanceof SerialApiError) return { key: errorMessageKey(error.code), detail: error.detail };
  return { key: 'error.failed', detail: {} };
}

/** 공개 작품 목록 — 최근 회차가 올라온 순. 로그인 여부가 바뀌면 mine 을 다시 받는다 */
export function useWorksFeed(api: SerialApi) {
  const signedIn = useSession() !== null;
  return useInfiniteQuery({
    queryKey: [KEY, 'works', signedIn],
    initialPageParam: '',
    queryFn: ({ pageParam }) => api.listWorks(pageParam),
    getNextPageParam: last => last.nextCursor || undefined,
    retry,
  });
}

export function useWork(api: SerialApi, id: string) {
  const signedIn = useSession() !== null;
  return useQuery({ queryKey: [KEY, 'work', id, signedIn], queryFn: () => api.getWork(id), retry });
}

export function useEpisode(api: SerialApi, id: string) {
  const signedIn = useSession() !== null;
  return useQuery({ queryKey: [KEY, 'episode', id, signedIn], queryFn: () => api.getEpisode(id), retry });
}

/** 내 작품 내리기. 성공하면 그 작품 캐시는 보는 화면이 떠날 때 버리고(다시 부르면 404) 나머지는 기다리지 않고 새로 고친다 — 화면 이동이 바로 된다 */
export function useRemoveWork(api: SerialApi) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      requireLogin();
      return api.removeWork(id);
    },
    onSuccess: (_, id) => {
      dropWhenUnwatched(client, [KEY, 'work', id]);
      void client.invalidateQueries({ queryKey: [KEY, 'works'] });
    },
  });
}

/** 내 회차 하나 내리기. 그 회차 캐시는 보는 화면이 떠날 때 버리고, 작품 상세·앞뒤 회차·목록은 기다리지 않고 새로 고친다 */
export function useRemoveEpisode(api: SerialApi) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      requireLogin();
      return api.removeEpisode(id);
    },
    onSuccess: (_, id) => {
      dropWhenUnwatched(client, [KEY, 'episode', id]);
      void client.invalidateQueries({ queryKey: [KEY], predicate: q => !(q.queryKey[1] === 'episode' && q.queryKey[2] === id) });
    },
  });
}

export function useCreateWork(api: SerialApi) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { title: string; synopsis: string; aiUsage: string }) => {
      requireLogin();
      return api.createWork(input);
    },
    onSuccess: () => client.invalidateQueries({ queryKey: [KEY, 'works'] }),
  });
}

/** 회차 공개. 성공하면 episode.publish 를 남긴다 — 가설 카드의 작가 연재 지속 지표 (HYPOTHESIS.md) */
export function usePublishEpisode(api: SerialApi, workId: string) {
  const client = useQueryClient();
  const track = useTrack();
  return useMutation({
    mutationFn: (input: { title: string; body: string }) => {
      requireLogin();
      return api.publishEpisode(workId, input);
    },
    onSuccess: ep => {
      track('episode.publish', { work_id: workId, no: ep.no });
      void client.invalidateQueries({ queryKey: [KEY] });
    },
  });
}
